<template>
  <div
    :data-cy="`deal-link-${result.id}`"
    class="relative flex flex-row items-center space-x-4 rounded-sm p-2"
    :class="{ 'bg-label/10': isActive }"
    @click="handleClick"
    @dblclick="handleDoubleClick"
    @mouseover="emit('deal:hover')"
  >
    <icon-base :icon="IconDeals" view-box="10 10 20 20" class="text-label" />
    <div class="flex flex-col">
      <div class="flex items-center justify-start space-x-2">
        <span class="text-headline capitalize">
          {{ result.application_name }}
        </span>
        <lf-tag size="xs">{{ result.id?.slice(0, 7) }}&hellip;</lf-tag>
        <lf-pill
          size="sm"
          :color-index="getColorIndex(result.current_stage_category)"
        >
          {{ result.current_stage_category }}
        </lf-pill>
      </div>
      <div class="flex items-center justify-start space-x-1 text-xs">
        <span class="capitalize">{{ result.workflow_template_name }},</span>
        <span>
          {{ $t("LOGS.CREATED") }}:
          {{ formatDateCustom(result.created_at) }}
        </span>
      </div>
    </div>
    <div
      v-if="isActive"
      class="absolute top-3-5 right-2 flex items-center space-x-1"
    >
      <button
        v-tooltip="`${$t('AI_AGENTS.OPEN_DEAL')}`"
        class="flex h-6 w-6 items-center justify-center rounded border border-divider bg-white hover:bg-grey-hover"
        @click.stop="emit('deal:navigate')"
      >
        <icon-base :icon="IconOpenSameTab" icon-color="none" />
      </button>
      <button
        v-tooltip="`${$t('AI_AGENTS.OPEN_DEAL_IN_NEW_TAB')}`"
        class="flex h-6 w-6 items-center justify-center rounded border border-divider bg-white hover:bg-grey-hover"
        @click.stop="emit('deal:new-tab')"
      >
        <icon-base :icon="IconOpenNewTab" icon-color="none" />
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";

import { STAGE_TYPE_COLOR_CODES } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";

import IconOpenNewTab from "@/components/icons/IconOpenNewTab.vue";
import IconOpenSameTab from "@/components/icons/IconOpenSameTab.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfPill from "@/components/ui/text/LfPill.vue";
import LfTag from "@/components/ui/text/LfTag.vue";

import type { AiSearchDeal } from "@/models/cmdK";
import type { StageCategoryType } from "@/models/workflows";

const CLICK_DELAY = 200;

const { result, isActive = false } = defineProps<{
  result: AiSearchDeal;
  isActive?: boolean;
}>();

const emit = defineEmits<{
  "deal:hover": [];
  "deal:click": [];
  "deal:navigate": [];
  "deal:new-tab": [];
}>();

const clickTimer = ref<number | null>(null);

const handleClick = () => {
  // Clear any existing timer
  if (clickTimer.value !== null) {
    clearTimeout(clickTimer.value);
  }

  // Set a timer to wait for potential double-click
  clickTimer.value = window.setTimeout(() => {
    // If we get here, it's a single click
    emit("deal:click");
    clickTimer.value = null;
  }, CLICK_DELAY);
};

const handleDoubleClick = () => {
  // Clear the single-click timer since this is a double-click
  if (clickTimer.value !== null) {
    clearTimeout(clickTimer.value);
    clickTimer.value = null;
  }

  emit("deal:navigate");
};

const getColorIndex = (category: string) =>
  STAGE_TYPE_COLOR_CODES[(category as StageCategoryType) || 0];
</script>
